/**
 * Discounts attached to a customer or a property, with add/remove for users
 * who can manage them. Shared by the customer and property detail pages.
 */

import React, { useState } from 'react';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { Badge, formatMoney } from './DetailWidgets';
import { inputClass, primaryButtonClass, rowDangerActionClass } from './formStyles';
import { Toast } from './Toast';

export function formatDiscountValue(discount: types.Discount): string {
  if (discount.type === types.DiscountType.PERCENTAGE) return `${Number(discount.value)}% off`;
  return `${formatMoney(discount.value)} off`;
}

export interface DiscountAttachmentPanelProps {
  idPrefix: string;
  /** null while the attached list is loading. */
  attached: types.Discount[] | null;
  loadError: string | null;
  catalog: types.Discount[];
  catalogError: string | null;
  canManage: boolean;
  emptyMessage: string;
  onAttach: (discountId: string) => Promise<void>;
  onDetach: (discountId: string) => Promise<void>;
}

export function DiscountAttachmentPanel({
  idPrefix,
  attached,
  loadError,
  catalog,
  catalogError,
  canManage,
  emptyMessage,
  onAttach,
  onDetach,
}: DiscountAttachmentPanelProps) {
  const [selectedId, setSelectedId] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const available = catalog.filter((d) => d.active && !attached?.some((a) => a.id === d.id));
  const isBusy = isAdding || removingId !== null;
  const selectId = `${idPrefix}-discount-select`;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedId) return;
    try {
      setIsAdding(true);
      setActionError(null);
      await onAttach(selectedId);
      setSelectedId('');
    } catch (err) {
      setActionError(getErrorMessage(err, 'Failed to add discount'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (discountId: string) => {
    try {
      setRemovingId(discountId);
      setActionError(null);
      await onDetach(discountId);
    } catch (err) {
      setActionError(getErrorMessage(err, 'Failed to remove discount'));
    } finally {
      setRemovingId(null);
    }
  };

  if (loadError) {
    return (
      <><Toast kind="error">{loadError}</Toast><p className="text-gray-500 text-sm">Discounts could not be loaded.</p></>
    );
  }

  if (attached === null) {
    return <p className="text-gray-500 text-sm">Loading discounts...</p>;
  }

  return (
    <div className="space-y-4">
      {actionError && <Toast kind="error">{actionError}</Toast>}

      {attached.length === 0 ? (
        <p className="text-gray-500 text-sm">{emptyMessage}</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {attached.map((discount) => (
            <li key={discount.id} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="text-gray-900 truncate">
                  {discount.name}
                  {!discount.active && (
                    <span className="ml-2 align-middle">
                      <Badge tone="gray">Inactive</Badge>
                    </span>
                  )}
                </p>
                <p className="text-sm text-gray-500">{formatDiscountValue(discount)}</p>
              </div>
              {canManage && (
                <button
                  onClick={() => handleRemove(discount.id)}
                  disabled={isBusy}
                  className={`flex-shrink-0 py-2.5 sm:py-0 ${rowDangerActionClass}`}
                >
                  {removingId === discount.id ? 'Removing...' : 'Remove'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <form onSubmit={handleAdd} className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label htmlFor={selectId} className="sr-only">
            Discount to add
          </label>
          <select
            id={selectId}
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={isBusy || available.length === 0}
            className={`${inputClass} sm:max-w-xs`}
          >
            <option value="">{available.length === 0 ? 'No other active discounts' : 'Select a discount'}</option>
            {available.map((discount) => (
              <option key={discount.id} value={discount.id}>
                {discount.name} ({formatDiscountValue(discount)})
              </option>
            ))}
          </select>
          <button type="submit" disabled={isBusy || !selectedId} className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`}>
            {isAdding ? 'Adding...' : 'Add Discount'}
          </button>
        </form>
      )}
      {canManage && catalogError && <Toast kind="error">{catalogError}</Toast>}
    </div>
  );
}
